import firebase from "firebase/app";
import uniqid from "uniqid";

import { useTranslation } from "react-i18next";

import { db } from "astrid-firebase";
import articleDeliveryTypes from "astrid-firestore/src/api/articles/constants/articleDeliveryTypes";
import getDistributionChannelsQuery from "astrid-firestore/src/api/distribution/getDistributionChannelsQuery";
import { DistributionPreset } from "astrid-firestore/src/api/distribution/types/DistributionPreset";
import updateOrganization from "astrid-firestore/src/api/organizations/updateOrganization";

import ArticleTypeSelect from "../../../articles/components/ArticleTypeSelect/ArticleTypeSelect";
import Form from "../../../forms/components/Form/Form";
import useSimpleForm from "../../../forms/hooks/useSimpleForm";

import DeliveryTypesSelect from "../DeliveryTypes/DeliveryTypesSelect";
import DistributionTargetDataForm from "../DistributionTargetDataForm/DistributionTargetDataForm";

export default function DistributionPresetForm({ publisher, preset, onClose }) {
	const { t } = useTranslation();

	const { form, onSubmit, error, setValue, watch } = useSimpleForm({
		schema: DistributionPreset,
		values: {
			channel: preset?.channel || null,
			articleType: preset?.articleType || null,
			languages: preset?.languages || publisher.languageOptions,
			imprintIds: preset?.imprintIds || [],
			deliveryTypes: preset?.deliveryTypes || [articleDeliveryTypes.ARTIFACT, articleDeliveryTypes.METADATA],
			targetData: {
				countries: preset?.targetData?.countries || [],
				prices: preset?.targetData?.prices || [],
				subChannels: preset?.targetData?.subChannels || [],
			},
			disabled: preset?.disabled || false,
		},
		onSubmit: (data) => {
			const id = preset?.id || uniqid();

			return updateOrganization(firebase, publisher.ref, {
				distribution: {
					presets: {
						[id]: {
							id,
							...data,
						},
					},
				},
			});
		},
		onSuccess: onClose,
	});

	const { channel } = watch();

	const onChangeOptions =
		(name) =>
		({ value }) => {
			if (value.length > 1 && value.includes("all")) {
				const last = value[value.length - 1];

				setValue(name, last === "all" ? ["all"] : value.filter((v) => v !== "all"));
			}
		};

	return (
		<Form.Modal
			form={form}
			header={preset ? t("editPreset", "Edit preset") : t("addPreset", "Add preset")}
			onSubmit={onSubmit}
			onClose={onClose}
			error={error}
		>
			<Form.CollectionSelect
				name="channel"
				label={t("channel", "Channel")}
				query={getDistributionChannelsQuery(firebase)}
				disabled={preset}
			/>

			<Form.Field name="articleType">
				<ArticleTypeSelect
					channel={channel}
					label={t("articleType", "Article type")}
					clearable
					disabled={!channel}
				/>
			</Form.Field>
			<DeliveryTypesSelect />
			<Form.LanguageSelect
				name="languages"
				label={t("languages", "Languages")}
				filterOptions={(option) =>
					!publisher?.languageOptions?.length || publisher?.languageOptions?.includes(option.value)
				}
				onChangeOptions={onChangeOptions("languages")}
				multiple
				clearable
				disabled={!channel}
			/>
			<Form.CollectionSelect
				name="imprintIds"
				label={t("imprints", "Imprints")}
				query={db
					.collection("imprints")
					.where("publisher.id", "==", publisher.id)
					.where("deleted", "==", false)}
				idOnly
				multiple
				clearable
				disabled={!channel}
			/>

			{channel && <DistributionTargetDataForm name="targetData" channel={channel} publisher={publisher} />}
			<Form.Checkbox name="disabled" label={t("disablePreset", "Disable preset")} />
		</Form.Modal>
	);
}
